import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import { gql } from '../../utils/graphql';

interface MediaProps {
	token: string;
}

interface MediaItem {
	id: string;
	name: string;
	url: string;
	folder: string | null;
	isActive: boolean;
	createdAt: string;
}

interface ImagePreviewProps {
	url: string;
	alt: string;
	onDimensionsLoaded: (width: number, height: number) => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ url, alt, onDimensionsLoaded }) => {
	const [isLoading, setIsLoading] = useState(true);

	return (
		<>
			{isLoading && <div className={styles.loader} />}
			<img
				src={url}
				alt={alt}
				style={{ display: isLoading ? 'none' : 'block' }}
				onLoad={(e) => {
					setIsLoading(false);
					const img = e.currentTarget;
					onDimensionsLoaded(img.naturalWidth, img.naturalHeight);
				}}
			/>
		</>
	);
};

interface VideoPreviewProps {
	url: string;
	onDimensionsLoaded: (width: number, height: number) => void;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ url, onDimensionsLoaded }) => {
	const videoRef = React.useRef<HTMLVideoElement>(null);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [isMobile, setIsMobile] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [shouldLoad, setShouldLoad] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia('(hover: none)');
		setIsMobile(mediaQuery.matches);
		const handleResize = () => setIsMobile(mediaQuery.matches);
		mediaQuery.addEventListener('change', handleResize);
		return () => mediaQuery.removeEventListener('change', handleResize);
	}, []);

	// Lazy load the video element when near viewport to free up network bandwidth
	useEffect(() => {
		if (!containerRef.current) return;
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setShouldLoad(true);
						observer.disconnect();
					}
				});
			},
			{ rootMargin: '200px' }
		);

		observer.observe(containerRef.current);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (!isMobile || !shouldLoad) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (!isLoading) {
							videoRef.current?.play().catch(() => { });
						}
					} else {
						videoRef.current?.pause();
						if (videoRef.current) videoRef.current.currentTime = 0;
					}
				});
			},
			{ threshold: 0.5 }
		);

		if (videoRef.current) observer.observe(videoRef.current);
		return () => observer.disconnect();
	}, [isMobile, isLoading, shouldLoad]);

	const handleMouseEnter = () => {
		if (!isMobile) videoRef.current?.play().catch(() => { });
	};

	const handleMouseLeave = () => {
		if (!isMobile) {
			videoRef.current?.pause();
			if (videoRef.current) videoRef.current.currentTime = 0;
		}
	};

	return (
		<div ref={containerRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
			{isLoading && <div className={styles.loader} />}
			{shouldLoad && (
				<video
					ref={videoRef}
					src={url}
					preload="metadata"
					muted
					playsInline
					loop
					style={{ display: isLoading ? 'none' : 'block' }}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onLoadedData={() => setIsLoading(false)}
					onLoadedMetadata={(e) => {
						setIsLoading(false);
						const video = e.currentTarget;
						onDimensionsLoaded(video.videoWidth, video.videoHeight);
					}}
				/>
			)}
		</div>
	);
};

interface DownloadState {
	id: string;
	progress: number;
	loadedBytes: number;
	totalBytes: number;
	status: 'downloading' | 'completed' | 'error';
}

const formatBytes = (bytes: number): string => {
	if (bytes === 0) return '0 B';
	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const Media: React.FC<MediaProps> = ({ token }) => {
	const [mediaList, setMediaList] = useState<MediaItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [activeFolder, setActiveFolder] = useState<string | null>(null);
	const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
	const [aspectRatios, setAspectRatios] = useState<Record<string, 'vertical' | 'horizontal' | 'square'>>({});
	const [downloadState, setDownloadState] = useState<Record<string, DownloadState>>({});

	useEffect(() => {
		fetchMedia();
	}, []);

	const fetchMedia = async () => {
		setLoading(true);
		try {
			const query = `
        query GetPublicMedia {
          publicMedia {
            id
            name
            url
            folder
            isActive
            createdAt
          }
        }
      `;
			const response = await gql(query, {}, token);
			if (response.data?.errors) throw new Error(response.data.errors[0].message);
			setMediaList(response.data?.data?.publicMedia || []);
		} catch (err: any) {
			console.error(err);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	if (loading) return <div className={styles.loading}>Se încarcă media...</div>;
	if (error) return <div className={styles.error}>Eroare: {error}</div>;

	const isVideo = (url: string) => url.match(/\.(mp4|webm|mov)$/i);
	const isImage = (url: string) => url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i);

	const handleDownload = async (item: MediaItem) => {
		const { id, url, name: filename } = item;
		if (downloadState[id]?.status === 'downloading') return;

		setDownloadState(prev => ({
			...prev,
			[id]: {
				id,
				progress: 0,
				loadedBytes: 0,
				totalBytes: 0,
				status: 'downloading'
			}
		}));

		try {
			const response = await fetch(url);
			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			const contentLength = response.headers.get('content-length');
			const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
			let loadedBytes = 0;
			let blob: Blob;

			if (response.body && typeof ReadableStream !== 'undefined') {
				const reader = response.body.getReader();
				const chunks: Uint8Array[] = [];

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					if (value) {
						chunks.push(value);
						loadedBytes += value.length;
						const progress = totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 0;
						setDownloadState(prev => ({
							...prev,
							[id]: {
								id,
								progress,
								loadedBytes,
								totalBytes,
								status: 'downloading'
							}
						}));
					}
				}
				blob = new Blob(chunks as BlobPart[]);
			} else {
				blob = await response.blob();
			}

			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);

			setDownloadState(prev => ({
				...prev,
				[id]: {
					id,
					progress: 100,
					loadedBytes: totalBytes || loadedBytes,
					totalBytes: totalBytes || loadedBytes,
					status: 'completed'
				}
			}));

			setTimeout(() => {
				setDownloadState(prev => {
					const copy = { ...prev };
					delete copy[id];
					return copy;
				});
			}, 2500);
		} catch (err) {
			console.error('Download failed', err);
			setDownloadState(prev => ({
				...prev,
				[id]: {
					id,
					progress: 0,
					loadedBytes: 0,
					totalBytes: 0,
					status: 'error'
				}
			}));

			window.open(url, '_blank');

			setTimeout(() => {
				setDownloadState(prev => {
					const copy = { ...prev };
					delete copy[id];
					return copy;
				});
			}, 3000);
		}
	};

	// Get unique folders
	const folders = Array.from(new Set(mediaList.map(m => m.folder).filter(Boolean))) as string[];
	const hasMultipleFolders = folders.length > 0;

	// Filter by active folder
	const filteredMedia = activeFolder === null
		? mediaList
		: activeFolder === '__none__'
			? mediaList.filter(m => !m.folder)
			: mediaList.filter(m => m.folder === activeFolder);

	const handleMediaLoad = (id: string, width: number, height: number) => {
		let ratio: 'vertical' | 'horizontal' | 'square' = 'horizontal';

		const aspect = width / height;

		if (aspect < 0.85) {
			ratio = 'vertical'; // < ~4:5 ratio
		} else if (aspect > 1.25) {
			ratio = 'horizontal'; // > ~5:4 ratio
		} else {
			ratio = 'square'; // In between is square-ish
		}

		setAspectRatios(prev => ({
			...prev,
			[id]: ratio
		}));
	};

	const renderMediaCard = (item: MediaItem) => {
		const ratio = aspectRatios[item.id];
		const isVertical = ratio === 'vertical';
		const isSquare = ratio === 'square';
		const itemDownload = downloadState[item.id];
		const isDownloading = itemDownload?.status === 'downloading';
		const isCompleted = itemDownload?.status === 'completed';
		const isError = itemDownload?.status === 'error';

		return (
			<div key={item.id} className={`${styles.mediaCard} ${isVertical ? styles.vertical : ''} ${isSquare ? styles.square : ''}`}>
				<div className={styles.mediaPreview}>
					{isImage(item.url) ? (
						<ImagePreview
							url={item.url}
							alt={item.name}
							onDimensionsLoaded={(w, h) => handleMediaLoad(item.id, w, h)}
						/>
					) : isVideo(item.url) ? (
						<VideoPreview
							url={item.url}
							onDimensionsLoaded={(w, h) => handleMediaLoad(item.id, w, h)}
						/>
					) : (
						<div className={styles.fileIcon}>📄</div>
					)}
				</div>
				<div className={styles.mediaInfo}>
					<div className={styles.mediaName}>{item.name.replace(/\.[^/.]+$/, "")}</div>
					<button
						onClick={() => handleDownload(item)}
						disabled={isDownloading}
						className={`${styles.downloadBtn} ${isDownloading ? styles.downloading : ''} ${isCompleted ? styles.completed : ''} ${isError ? styles.errorBtn : ''}`}
						style={{ border: 'none', cursor: isDownloading ? 'wait' : 'pointer', width: '100%', fontFamily: 'inherit' }}
					>
						{isDownloading && (
							<div
								className={styles.progressBar}
								style={{ width: `${Math.max(itemDownload.progress, 5)}%` }}
							/>
						)}
						<span className={styles.btnContent}>
							{isDownloading ? (
								<>
									<span className={styles.btnSpinner} />
									<span>
										{itemDownload.totalBytes > 0
											? `${itemDownload.progress}% (${formatBytes(itemDownload.loadedBytes)} / ${formatBytes(itemDownload.totalBytes)})`
											: `Se descarcă... ${formatBytes(itemDownload.loadedBytes)}`
										}
									</span>
								</>
							) : isCompleted ? (
								<>
									<span className={styles.checkIcon}>✓</span> Descărcat!
								</>
							) : isError ? (
								'Reîncearcă descărcarea'
							) : (
								'Descarcă'
							)}
						</span>
					</button>
				</div>
			</div>
		);
	};

	return (
		<div className={styles.mediaContainer}>
			<h2 className={styles.sectionTitle}>Materiale Media</h2>
			<p className={styles.sectionDesc}>
				Descarcă materiale promoționale pentru a le folosi în postările tale.
			</p>

			{/* Folder filter dropdown */}
			{hasMultipleFolders && (
				<div className={styles.mediaFolderSelect}>
					<button
						className={styles.mediaFolderDropdownBtn}
						onClick={() => setFolderDropdownOpen(!folderDropdownOpen)}
					>
						{activeFolder ? `📁 ${activeFolder}` : 'Toate'}
						<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#EFA43B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: folderDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
							<path d="M6 9l6 6 6-6" />
						</svg>
					</button>

					{folderDropdownOpen && (
						<>
							<div className={styles.dropdownBackdrop} onClick={() => setFolderDropdownOpen(false)} />
							<div className={styles.mediaFolderDropdownList}>
								<div
									className={`${styles.mediaFolderOption} ${activeFolder === null ? styles.active : ''}`}
									onClick={() => {
										setActiveFolder(null);
										setFolderDropdownOpen(false);
									}}
								>
									Toate
								</div>
								{folders.map((folder) => (
									<div
										key={folder}
										className={`${styles.mediaFolderOption} ${activeFolder === folder ? styles.active : ''}`}
										onClick={() => {
											setActiveFolder(folder);
											setFolderDropdownOpen(false);
										}}
									>
										📁 {folder}
									</div>
								))}
							</div>
						</>
					)}
				</div>
			)}

			{filteredMedia.length === 0 ? (
				<div className={styles.emptyState}>Nu există materiale media disponibile momentan.</div>
			) : (
				<div className={styles.mediaGrid}>
					{filteredMedia.map(renderMediaCard)}
				</div>
			)}
		</div>
	);
};

export default Media;
